import React, { useEffect } from "react";
import { Wrench, Sparkles } from "lucide-react";

export const MaintenanceDialog: React.FC = () => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const blockKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", blockKey, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", blockKey, true);
    };
  }, []);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-title"
      aria-describedby="maintenance-desc"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-md" />

      <div className="relative w-full max-w-lg animate-fadeIn">
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 opacity-80 blur-md animate-pulse" />

        <div className="relative rounded-2xl bg-white/95 dark:bg-gray-900/95 border border-white/40 dark:border-white/10 shadow-2xl overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br from-blue-400/30 to-purple-500/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-gradient-to-br from-pink-400/30 to-purple-500/30 blur-3xl" />

          <div className="relative p-8 sm:p-10 text-center">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 blur-xl opacity-60 animate-pulse" />
                <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 p-0.5">
                  <div className="w-full h-full rounded-full bg-white dark:bg-gray-900 flex items-center justify-center">
                    <Wrench
                      size={36}
                      className="text-transparent bg-clip-text bg-gradient-to-br from-blue-500 to-purple-600"
                      style={{
                        stroke: "url(#maintenance-gradient)",
                      }}
                    />
                    <svg width="0" height="0" className="absolute">
                      <defs>
                        <linearGradient
                          id="maintenance-gradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#8b5cf6" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
                <Sparkles
                  size={18}
                  className="absolute -top-1 -right-1 text-yellow-400 animate-pulse"
                  fill="currentColor"
                />
              </div>
            </div>

            <h2
              id="maintenance-title"
              className="text-2xl sm:text-3xl font-bold mb-3 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
            >
              We'll Be Right Back
            </h2>

            <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200/60 dark:border-blue-700/40">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300 tracking-wide uppercase">
                Maintenance In Progress
              </span>
            </div>

            <p
              id="maintenance-desc"
              className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2"
            >
              The{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                Algorand Agency
              </span>{" "}
              system is undergoing a heavy upgrade and maintenance.
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              We're polishing things up and will be available again very soon.
              Thank you for your patience.
            </p>

            <div className="mt-7 flex items-center justify-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" />
              <span
                className="w-2 h-2 rounded-full bg-purple-500 animate-bounce"
                style={{ animationDelay: "120ms" }}
              />
              <span
                className="w-2 h-2 rounded-full bg-pink-500 animate-bounce"
                style={{ animationDelay: "240ms" }}
              />
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200/70 dark:border-gray-700/60">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Follow updates at{" "}
                <a
                  href="https://goplausible.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  goplausible.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
